import { useEffect, useState } from "react";
import "./AdminHandleIntegrations.scss";
import { listCourts } from "../../api/courts/courts";
import { toast } from "react-toastify";
import {
  ICourt,
  ILightsCourtStatus,
  ILightsStatus,
  IVisualArtBooking,
} from "../../types/api";
import { getLightsStatus, testLightsConnection } from "../../api/lights/lights";
import Spinner from "../../components/UI/Spinner/Spinner";
import {
  listVisualArtBookings,
  updateVisualArtBooking,
} from "../../api/visualArt/visualArt";
import LocalRecomendationsPopup from "../../containers/Recomendations/LocalRecomendationsPopup";
import { ROUTE } from "../../shared/enums";
import screen from "../../assets/images/screen.svg";

interface IAdminHandleIntegrationsProps {}

interface IBookingState {
  active: boolean;
  info: string;
  isDouble: boolean;
  player_one_first_team: string;
  player_one_second_team: string;
  player_two_first_team: string;
  player_two_second_team: string;
}

function AdminHandleIntegrations(props: IAdminHandleIntegrationsProps) {
  const {} = props;
  const [courts, setCourts] = useState<ICourt[]>([]);
  const [visualArtBookings, setVisualArtBookings] = useState<
    IVisualArtBooking[]
  >([]);
  const [bookingStates, setBookingStates] = useState<{
    [key: string]: IBookingState;
  }>({});
  const [originalBookingStates, setOriginalBookingStates] = useState<{
    [key: string]: IBookingState;
  }>({});

  const [currentTab, setCurrentTab] = useState<"BADGES" | "LIGHTS">("BADGES");
  const [lights, setLights] = useState<ILightsStatus | null>(null);
  const [lightsLoading, setLightsLoading] = useState<boolean>(false);
  const [testingLights, setTestingLights] = useState<boolean>(false);

  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [showEditRecomedationsPopup, setShowEditRecomedationsPopup] =
    useState<boolean>(false);
  const fetchCourts = async () => {
    try {
      const courtsResponse = await listCourts();
      if (courtsResponse !== null && "message" in courtsResponse) {
        return;
      }
      setCourts(courtsResponse);
    } catch (error: any) {
      toast.error(`${error}`);
    }
  };

  const fetchVisualArtBookings = async () => {
    try {
      const response = await listVisualArtBookings();
      if (response !== null && "message" in response) {
        console.error(response.message);
        return;
      }
      setVisualArtBookings(response);
    } catch (error: any) {
      toast.error(`${error}`);
    }
  };

  // The integration names courts Bana_01 and Bana_CC; members know them as
  // B1 and CC.
  const courtLabel = (knxName: string) => {
    const suffix = knxName.replace("Bana_", "");
    return /^\d+$/.test(suffix) ? `B${parseInt(suffix, 10)}` : suffix;
  };

  // A lit court is a yellow bulb, a dark one grey; a value that could not be
  // read is neither, so it stays a dash.
  const bulb = (on: boolean | null) =>
    on === null ? (
      <span className="lights-unknown">–</span>
    ) : (
      <i className={`fa-solid fa-lightbulb lights-bulb ${on ? "on" : "off"}`} />
    );

  // With no route to the controller nothing can be judged, so the verdict is a
  // dash rather than fifteen red crosses for courts that may well be fine.
  const verdict = (ok: boolean, known = true) =>
    !known ? (
      <span className="lights-unknown">–</span>
    ) : ok ? (
      <i className="fa-regular fa-check lights-verdict ok" />
    ) : (
      <i className="fa-regular fa-xmark lights-verdict not-ok" />
    );

  // Centre court first, then the numbered courts by number.
  const courtRank = (name: string) =>
    /^B\d+$/.test(name) ? [1, parseInt(name.slice(1), 10)] : [0, 0];

  // The rows are the indoor courts themselves, so the table is complete before
  // any check returns; the status columns fill in when it does.
  const indoorCourts = [...courts]
    .filter((court) => court.court_type === "INDOOR")
    .sort((a, b) => {
      const [groupA, numberA] = courtRank(a.name);
      const [groupB, numberB] = courtRank(b.name);
      return groupA - groupB || numberA - numberB;
    });

  const statusFor = (courtId: string): ILightsCourtStatus | undefined =>
    lights?.courts.find((court) => court.court_id === courtId);

  // Reaches Novemberbolaget's API once per court, so it runs on request
  // rather than on every visit to the page.
  const fetchLights = async () => {
    setLightsLoading(true);
    setLights(await getLightsStatus());
    setLightsLoading(false);
  };

  // Writes to the test court and reads it back. The result replaces what the
  // status check last read for that court, so the row stays truthful.
  const runConnectionTest = async () => {
    setTestingLights(true);
    const result = await testLightsConnection();
    if (result) {
      toast[result.ok ? "success" : "error"](
        result.ok
          ? "Anslutningen fungerar — testbanan svarade på ändringen"
          : "Testbanan svarade inte som väntat",
      );
      setLights((previous) =>
        previous
          ? {
              ...previous,
              test_court_actual: result.after,
              connection_ok: result.ok || previous.connection_ok,
            }
          : previous,
      );
    }
    setTestingLights(false);
  };

  const loadRecommendations = () => {
    const storedRecommendations = localStorage.getItem("adminRecommendations");
    if (storedRecommendations) {
      setRecommendations(JSON.parse(storedRecommendations));
    }
  };

  useEffect(() => {
    fetchCourts();
    fetchVisualArtBookings();
    loadRecommendations();
    fetchLights();
  }, []);

  const hasChanges = (court_id: string) => {
    const original = originalBookingStates[court_id];
    const current = bookingStates[court_id];
    return (
      original.active !== current.active ||
      original.info !== current.info ||
      original.isDouble !== current.isDouble ||
      original.player_one_first_team !== current.player_one_first_team ||
      original.player_one_second_team !== current.player_one_second_team ||
      original.player_two_first_team !== current.player_two_first_team ||
      original.player_two_second_team !== current.player_two_second_team
    );
  };
  useEffect(() => {
    const initialBookingStates = visualArtBookings.reduce((acc, booking) => {
      const isDouble =
        !!booking.player_two_first_team || !!booking.player_two_second_team;

      return {
        ...acc,
        [booking.court_id]: {
          active: booking.active,
          info: booking.info || "",
          isDouble: isDouble,
          player_one_first_team: booking.player_one_first_team,
          player_one_second_team: booking.player_one_second_team,
          player_two_first_team: booking.player_two_first_team || "",
          player_two_second_team: booking.player_two_second_team || "",
        },
      };
    }, {});

    setBookingStates(initialBookingStates);
    setOriginalBookingStates(initialBookingStates);
  }, [visualArtBookings]);

  const handleInputChange = (
    court_id: string,
    field: string,
    value: string | boolean,
  ) => {
    setBookingStates((prevStates) => {
      const updatedBookingState = {
        ...prevStates[court_id],
        [field]: value,
      };
      if (field === "isDouble" && value === false) {
        updatedBookingState.player_two_first_team = "";
        updatedBookingState.player_two_second_team = "";
      }

      return {
        ...prevStates,
        [court_id]: updatedBookingState,
      };
    });
  };

  const handleUpdateVisualArtBooking = async (
    court_id: string,
    onlyUpdate: boolean,
  ) => {
    try {
      const bookingState = bookingStates[court_id];
      if (!bookingState) {
        return;
      }
      let updatedActiveStatus = false;
      if (onlyUpdate) {
        updatedActiveStatus = bookingState.active;
      } else {
        updatedActiveStatus = !bookingState.active;
      }

      const repsonse = await updateVisualArtBooking(
        updatedActiveStatus,
        court_id,
        bookingState.info,
        bookingState.player_one_first_team,
        bookingState.player_one_second_team,
        bookingState.player_two_first_team,
        bookingState.player_two_second_team,
      );

      if (repsonse !== null && "message" in repsonse) {
        toast.error(repsonse.message);
        return;
      }

      setBookingStates((prevStates) => ({
        ...prevStates,
        [court_id]: {
          ...prevStates[court_id],
          active: updatedActiveStatus,
        },
      }));

      setOriginalBookingStates((prevStates) => ({
        ...prevStates,
        [court_id]: {
          active: updatedActiveStatus,
          info: bookingState.info,
          isDouble: bookingState.isDouble,
          player_one_first_team: bookingState.player_one_first_team,
          player_one_second_team: bookingState.player_one_second_team,
          player_two_first_team: bookingState.player_two_first_team,
          player_two_second_team: bookingState.player_two_second_team,
        },
      }));

      toast.success(
        `Booking ${
          updatedActiveStatus ? "activated" : "deactivated"
        } successfully!`,
      );
    } catch (error: any) {
      toast.error(`${error}`);
    }
  };

  if (
    courts.length === 0 ||
    visualArtBookings.length === 0 ||
    Object.keys(bookingStates).length === 0
  ) {
    return <Spinner />;
  }

  return (
    <div className="admin-handle-integrations-layout">
      <h1 className="text-l primary-text">Integrationer</h1>
      <div className="integrations-actions-wrapper">
        <div className="integrations-actions-left">
          <button
            className={`toggle-button text-m ${
              currentTab === "BADGES" ? "active" : ""
            }`}
            onClick={() => setCurrentTab("BADGES")}
          >
            Namnskyltar
          </button>{" "}
          <button
            className={`toggle-button text-m ${
              currentTab === "LIGHTS" ? "active" : ""
            }`}
            onClick={() => setCurrentTab("LIGHTS")}
          >
            Ljus på banor
          </button>
        </div>

        <div className="integrations-actions-right text-m">
          <div>
            {currentTab === "BADGES" ? (
              <button onClick={() => setShowEditRecomedationsPopup(true)}>
                Redigera sparade namn
              </button>
            ) : (
              <button onClick={fetchLights} disabled={lightsLoading}>
                {lightsLoading ? "Kontrollerar…" : "Kontrollera igen"}
              </button>
            )}
          </div>
        </div>
      </div>
      {currentTab === "LIGHTS" && (
        <div className="lights-status-wrapper">
          {lights && !lights.connection_ok && (
            <div className="lights-status-summary text-m">
              <span className="lights-connection-down">
                Ingen kontakt med ljusstyrningen — värdena nedan går inte att
                lita på.
              </span>
            </div>
          )}
          <div className="admin-handle-integrations-overflow-wrapper">
            <table className="lights-status-table text-m">
              <thead>
                <tr className="text-m secondary-text">
                  <th>Bana</th>
                  <th>Tid</th>
                  <th>Enligt Volley</th>
                  <th>Enligt Novemberbolaget</th>
                  <th>Sync</th>
                </tr>
              </thead>
              <tbody>
                {indoorCourts.map((court) => {
                  const status = statusFor(court.court_id);
                  return (
                    <tr key={court.court_id}>
                      <td>{court.name}</td>
                      <td>
                        {!status
                          ? ""
                          : status.slot_start
                            ? `${status.slot_start.slice(
                                0,
                                5,
                              )}–${(status.slot_end ?? "").slice(0, 5)}`
                            : "Ingen tid"}
                      </td>
                      <td>{status ? bulb(status.expected) : ""}</td>
                      <td>{status ? bulb(status.actual) : ""}</td>
                      <td>
                        {status
                          ? verdict(status.in_sync, lights!.connection_ok)
                          : ""}
                      </td>
                    </tr>
                  );
                })}
                <tr className="lights-spacer-row">
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
                <tr className="lights-test-row">
                  <td>
                    {lights ? courtLabel(lights.test_court_name) : "B99"}
                    <span className="secondary-text"> (test)</span>
                  </td>
                  <td>–</td>
                  <td>–</td>
                  <td>{lights ? bulb(lights.test_court_actual) : ""}</td>
                  <td>
                    <button
                      type="button"
                      className="lights-test-button text-s"
                      onClick={runConnectionTest}
                      disabled={testingLights}
                    >
                      {testingLights ? "Testar…" : "Testa anslutning"}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
      {currentTab === "BADGES" && (
        <div className="admin-handle-integrations-overflow-wrapper">
          <table className="visual-art-integrations-table text-m">
            <thead>
              <tr className="text-m secondary-text">
                <th>Digital skärm</th>
                <th>Bana</th>
                <th>Inställning</th>
                <th>
                  <span className="text-color-blue">Team 1 </span> - spelare 1
                </th>
                <th>
                  <span className="text-color-blue">Team 1 </span> - spelare 2
                </th>
                <th>
                  <span className="text-color-red">Team 2 </span> - spelare 1
                </th>
                <th>
                  <span className="text-color-red">Team 2 </span> - spelare 2
                </th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visualArtBookings.map((booking) => {
                const courtName = courts.find(
                  (court) => court.court_id === booking.court_id,
                )?.name;
                const bookingState = bookingStates[booking.court_id] || {
                  active: false,
                  isDouble: false,
                  player_one_first_team: "",
                  player_one_second_team: "",
                  player_two_first_team: "",
                  player_two_second_team: "",
                };
                const namesAreNotEmpty = bookingState.isDouble
                  ? bookingState.player_one_first_team !== "" &&
                    bookingState.player_one_second_team !== "" &&
                    bookingState.player_two_first_team !== "" &&
                    bookingState.player_two_second_team !== ""
                  : bookingState.player_one_first_team !== "" &&
                    bookingState.player_one_second_team !== "";

                return (
                  <tr
                    key={booking.court_id}
                    className={bookingState.active ? "active-row" : ""}
                  >
                    <td>
                      <a
                        href={
                          ROUTE.VisualArtPreview +
                          `?court_id=${booking.court_id}`
                        }
                        target="_blank"
                      >
                        <img src={screen} />
                      </a>
                    </td>
                    <td>{courtName}</td>
                    <td>
                      <div className="setting-toggle-wrapper">
                        <span className="text-m">
                          {bookingState.isDouble ? "Dubbel" : "Singel"}
                        </span>
                        <label className="toggle text-m">
                          <input
                            type="checkbox"
                            name="setting-toggle"
                            checked={bookingState.isDouble}
                            onChange={(e) =>
                              handleInputChange(
                                booking.court_id,
                                "isDouble",
                                e.target.checked,
                              )
                            }
                          />

                          <span className="slider round"></span>
                        </label>
                      </div>
                    </td>
                    <td>
                      <input
                        className="player-input"
                        placeholder="Namn"
                        value={bookingState.player_one_first_team}
                        list="recommendationList"
                        onChange={(e) =>
                          handleInputChange(
                            booking.court_id,
                            "player_one_first_team",
                            e.target.value,
                          )
                        }
                      />
                    </td>
                    <td>
                      <input
                        className={`player-input ${
                          bookingState.isDouble ? "" : "disabled-input"
                        }`}
                        placeholder={bookingState.isDouble ? "Namn" : "Namn"}
                        disabled={!bookingState.isDouble}
                        value={bookingState.player_two_first_team}
                        onChange={(e) =>
                          handleInputChange(
                            booking.court_id,
                            "player_two_first_team",
                            e.target.value,
                          )
                        }
                        list="recommendationList"
                      />
                    </td>
                    <td>
                      <input
                        className="player-input"
                        placeholder="Namn"
                        value={bookingState.player_one_second_team}
                        onChange={(e) =>
                          handleInputChange(
                            booking.court_id,
                            "player_one_second_team",
                            e.target.value,
                          )
                        }
                        list="recommendationList"
                      />
                    </td>

                    <td>
                      <input
                        className={`player-input ${
                          bookingState.isDouble ? "" : "disabled-input"
                        }`}
                        placeholder={bookingState.isDouble ? "Namn" : "Namn"}
                        disabled={!bookingState.isDouble}
                        value={bookingState.player_two_second_team}
                        onChange={(e) =>
                          handleInputChange(
                            booking.court_id,
                            "player_two_second_team",
                            e.target.value,
                          )
                        }
                        list="recommendationList"
                      />
                    </td>
                    <td>
                      <button
                        className={`toggle-button ${
                          bookingState.active ? "active-disable" : "inactive"
                        } text-m`}
                        onClick={() =>
                          handleUpdateVisualArtBooking(booking.court_id, false)
                        }
                        disabled={!namesAreNotEmpty && !bookingState.active}
                      >
                        {bookingState.active ? "Avaktivera" : "Aktivera"}
                      </button>
                    </td>
                    <td>
                      <button
                        className={`toggle-button ${"inactive"} text-m`}
                        onClick={() =>
                          handleUpdateVisualArtBooking(booking.court_id, true)
                        }
                        disabled={!hasChanges(booking.court_id)}
                      >
                        Uppdatera
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <datalist id="recommendationList">
            {recommendations.map((item, index) => (
              <option key={index} value={item} />
            ))}
          </datalist>
        </div>
      )}
      <LocalRecomendationsPopup
        showPopup={showEditRecomedationsPopup}
        onClose={() => setShowEditRecomedationsPopup(false)}
        onConfirm={(updatedRecommendations) =>
          setRecommendations(updatedRecommendations)
        }
        localRecommendations={recommendations}
        localStorageKey="adminRecommendations"
      />
    </div>
  );
}

export default AdminHandleIntegrations;
